import { appendLayoutPackageAPI, exportLayoutPackageAPI } from '@/api/layout';
import BaseTextField from '@/components/form/BaseTextField';
import BigIconButton from '@/components/layout/BigIconButton';
import LayoutSubheading from '@/components/layout/LayoutSubheading';
import RegenerateDialog from '@/components/layout/RegenerateDialog';
import SaveAsDialog from '@/components/layout/SaveAsDialog';
import TabLayouts from '@/components/layout/TabLayouts';
import BasePaper from '@/components/ui/BasePaper';
import BaseTitle from '@/components/ui/BaseTitle';
import { useAppContext } from '@/provider/contexts';
import { useLayoutState } from '@/provider/layouts/layoutContext';
import { LayoutProvider } from '@/provider/layouts/LayoutProvider';
import { BackupTable, BurstMode, Download } from '@mui/icons-material';
import { Box, CircularProgress } from '@mui/material';
import localforage from 'localforage';
import ButtonGroup from '@/components/layout/ButtonGroup';
import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLayoutUtils } from '@/hooks/layouts/useLayoutUtils';
import { getIsLayoutChange } from '@/utils/layouts/layoutUtils';
import { isEmpty } from 'lodash';
import { useTranslation } from 'react-i18next';
import PreviewLayoutErrorDialog from '@/components/layout/PreviewLayoutError';

const LayoutPackage = () => {
    const { t } = useTranslation('common');
    const navigate = useNavigate();
    const { setNotification } = useAppContext();
    const { id, isNew, autoGeneratePreview, getSinglePayload, getMultiPayload } = useLayoutUtils();
    const { state, dispatch } = useLayoutState();

    // Calculate
    const cancelDisabled = useMemo(
        () => state.loadings.saveAs || state.loadings.regenerate || state.loadings.save,
        [state.loadings.saveAs, state.loadings.regenerate, state.loadings.save],
    );
    const saveDisabled = useMemo(() => isNew || cancelDisabled, [isNew, cancelDisabled]);
    const saveAsDisabled = useMemo(() => {
        const isPackageEmpty = isEmpty(state.single.layoutsById) && isEmpty(state.multi.layoutsById);
        const isNewEmpty = isNew && isEmpty(state.packageName);
        return isPackageEmpty || cancelDisabled || isNewEmpty;
    }, [isNew, state.single.layoutsById, state.multi.layoutsById, cancelDisabled, state.packageName]);

    // Method
    const handleSave = async () => {
        if (isNew) return;
        const isLayoutChange = getIsLayoutChange(state.single, state.multi);
        if (!isLayoutChange) {
            setNotification({ message: t('No changes to save.'), severity: 'info' });
            return;
        }
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'save', loading: true } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            const packageId = Number(id);

            const payload = {
                package_description: '',
                packages: {
                    ...getSinglePayload(),
                    ...getMultiPayload(),
                },
            };

            await appendLayoutPackageAPI(corpId!, packageId, payload);

            setNotification({ message: t('Layouts appended successfully!'), severity: 'success' });
            await autoGeneratePreview(packageId);
        } catch (err) {
            console.error(err);
            setNotification({ message: t('Failed to save layouts.'), severity: 'error' });
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'save', loading: false } });
        }
    };

    const handleExport = async () => {
        if (state.loadings.export) return;
        dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'export', loading: true } });
        try {
            const corpId = await localforage.getItem<number>('corporation_id');
            if (!corpId || !id) return;
            const { data } = await exportLayoutPackageAPI(corpId, +id);
            const link = data.data.download_link;
            if (link) window.open(link, '_blank');
            else setNotification({ message: t('No download link returned.'), severity: 'error' });
        } catch {
            setNotification({ message: t('Export failed.'), severity: 'error' });
        } finally {
            dispatch({ type: 'TOGGLE_LOADING', payload: { key: 'export', loading: false } });
        }
    };

    const openRegenerateDialog = async () => {
        if (isNew || state.isDirty) {
            setNotification({
                message: t('Please save your changes before regenerating the preview.'),
                severity: 'warning',
            });
            return;
        }
        await autoGeneratePreview(Number(id));
    };

    return (
        <>
            <BasePaper style={{ display: 'flex', flexDirection: 'column' }}>
                <BaseTitle
                    icon={
                        <Box sx={{ pt: '3px' }}>
                            <BackupTable />
                        </Box>
                    }
                    title={isNew ? t('New Layout Package') : t('Edit Layout Package')}
                />
                <Box>
                    <ButtonGroup
                        isNew={isNew}
                        onBack={() => navigate('/admin/layouts', { replace: true })}
                        onSave={handleSave}
                        saveDisabled={saveDisabled}
                        saveLoading={state.loadings.save}
                        onSaveAs={() => {
                            dispatch({ type: 'SET_SAVE_AS_NAME', payload: state.packageName });
                            dispatch({ type: 'TOGGLE_DIALOG', payload: { key: 'saveAs', open: true } });
                        }}
                        saveAsDisabled={saveAsDisabled}
                        saveAsLoading={state.loadings.saveAs}
                        onCancel={() => navigate(-1)}
                        cancelDisabled={cancelDisabled}
                    />

                    <Box sx={{ display: { xs: 'block', md: 'flex' }, mt: 2 }}>
                        <Box sx={{ flex: 1, mr: { xs: 0, md: 1 }, mb: { xs: 1, md: 0 } }}>
                            <LayoutSubheading>{t('Package Name:')}</LayoutSubheading>
                            <BaseTextField
                                fullWidth
                                value={state.packageName}
                                disabled={!isNew}
                                placeholder={t('Layout Package Name')}
                                onChange={(_event) =>
                                    dispatch({ type: 'SET_PACKAGE_NAME', payload: _event.target.value })
                                }
                                sx={{ my: 1 }}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: { xs: '100%', md: '50%' },
                                display: 'flex',
                                justifyContent: { xs: 'flex-start', md: 'flex-end' },
                                pt: { xs: 0, md: 2.5 },
                                alignItems: 'center',
                                gap: 1.5,
                            }}
                        >
                            <BigIconButton
                                tooltipTitle={state.loadings.export ? t('Exporting...') : t('Export layouts')}
                                onClick={handleExport}
                                disabled={state.loadings.export}
                            >
                                {state.loadings.export ? <CircularProgress size={24} /> : <Download />}
                            </BigIconButton>

                            <BigIconButton
                                tooltipTitle={t('Regenerate layout preview')}
                                onClick={openRegenerateDialog}
                                disabled={state.loadings.layout}
                            >
                                <BurstMode />
                            </BigIconButton>
                        </Box>
                    </Box>
                </Box>
                <Box sx={{ display: 'flex', flex: 1, justifyContent: 'center' }}>
                    <TabLayouts />
                </Box>
            </BasePaper>
            <SaveAsDialog />
            <RegenerateDialog />
            <PreviewLayoutErrorDialog />
        </>
    );
};

const LayoutPackagePage = () => {
    return (
        <LayoutProvider>
            <LayoutPackage />
        </LayoutProvider>
    );
};

export default LayoutPackagePage;
